import React from "react";
import {
  Box,
  Flex,
  Label as BaseLabel,
  Radio,
  Span,
  styled,
  themeGet,
} from "@catchoftheday/cg-components";
import { formatPrice } from "@catchoftheday/utilities";
import { OnePassOptionFragment } from "@common-vendors/generated/graphql";

type PlanOptionRadioProps = {
  /** OnePass subscription option/plan */
  onePassSubscriptionOption: OnePassOptionFragment;
  /** callback when an option is selected, mainly to handle keyboard event */
  onChange: (value: string) => void;
  /** flag to indicate that this option is selected */
  selected?: boolean;
  /** flag to indicate that this option is disabled */
  disabled?: boolean;
  /** flag to indicate whether customer is eligible for free trial */
  isFreeTrialEligible?: boolean;
};

const Label = styled(BaseLabel)`
  &:hover {
    & > * {
      color: ${themeGet("colors.textWhite")};
      background-color: ${themeGet("colors.onePass.primaryDarker")};
      border: 2px solid ${themeGet("colors.onePass.primaryDarker")};
    }
  }

  &:focus-visible {
    border: none;
    outline: none;

    & > * {
      color: ${themeGet("colors.textWhite")};
      background-color: ${themeGet("colors.onePass.primaryDarker")};
      border: 2px solid ${themeGet("colors.onePass.primaryDarker")};
    }
  }

  &:active {
    & > * {
      color: ${themeGet("colors.textWhite")};
      background-color: ${themeGet("colors.onePass.primaryDarker")};
      box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.25);
    }
  }
`;

export const PlanOptionRadio = ({
  onePassSubscriptionOption,
  onChange,
  selected,
  disabled,
  isFreeTrialEligible,
}: PlanOptionRadioProps) => {
  const { interval, price } = onePassSubscriptionOption;
  const { amount } = price;
  return (
    <Label
      key={`input-radio-${onePassSubscriptionOption.id}`}
      htmlFor={`input-radio-${onePassSubscriptionOption.id}`}
      tabIndex={0}
      width="50%"
      aria-label={`OnePass ${interval} subscription plan at ${formatPrice(
        onePassSubscriptionOption.price.amount
      )} per ${interval === "MONTHLY" ? "month" : "year"}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !disabled) {
          onChange(onePassSubscriptionOption.id);
        }
      }}
    >
      <Flex
        height="58px"
        borderStyle="solid"
        borderColor={disabled ? "ui.borderColor" : "onePass.primaryDarker"}
        borderWidth="2px"
        position="relative"
        boxShadow={selected ? "box" : undefined}
        backgroundColor={disabled ? "ui.borderColor" : undefined}
        borderRadius="8px"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        cursor={disabled ? "not-allowed" : "pointer"}
      >
        <Box textTransform="uppercase">
          <Span fontWeight="semibold" fontSize={{ xs: "xs", md: "sm" }}>
            {formatPrice(amount, false)}
            {interval === "YEARLY" ? "/YEAR" : "/MONTH"}
            <br />
            {isFreeTrialEligible ? "Start Free Trial" : "Join Now"}
          </Span>
        </Box>
      </Flex>
      <Box display="none">
        <Radio
          id={`input-radio-${onePassSubscriptionOption.id}`}
          value={onePassSubscriptionOption.id}
          disabled={disabled}
        />
      </Box>
    </Label>
  );
};
